/**
 * Create / edit / delete reminders from the firm calendar. An entry belongs
 * to the client picked in the form, or to the firm when none is picked (a
 * team meeting). Mirrors the client Reminders tab's `useRecordCrud` but is
 * not bound to one client: mutations go to the entry's client's API, or the
 * firm's. After any change the firm calendar queries (and dashboards) refetch.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import {
  useForm,
  type SubmitHandler,
  type UseFormReturn
} from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import {
  LawFirmRemindersAPI,
  type LawFirmReminder
} from "@/api/lawFirmReminders";
import type {
  ReminderStatus,
  UpdateForeignNationalReminderRequest
} from "@/api/types/foreignNationalReminder";
import { queryClient } from "@/lib/query/queryClient";

import {
  emptyReminderDraft,
  reminderDraftSchema,
  reminderDraftToPayload,
  reminderToDraft,
  type ReminderDraft
} from "./remindersModel";

export type FirmReminderDraft = ReminderDraft & {
  /** The client the entry belongs to; empty for a firm entry. */
  clientId: string;
};

export const firmReminderDraftSchema: z.ZodType<
  FirmReminderDraft,
  FirmReminderDraft
> = z.intersection(
  reminderDraftSchema,
  z.object({ clientId: z.string().trim() })
) as unknown as z.ZodType<FirmReminderDraft, FirmReminderDraft>;

const updateEntry = (
  record: LawFirmReminder,
  patch: UpdateForeignNationalReminderRequest
) =>
  record.foreign_national_id
    ? ForeignNationalsAPI.updateReminder(
        record.foreign_national_id,
        record.reminder_id,
        patch
      )
    : LawFirmRemindersAPI.update(record.reminder_id, patch);

export const emptyFirmReminderDraft = (): FirmReminderDraft => ({
  ...emptyReminderDraft(),
  clientId: ""
});

export type FirmReminderEditor =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; record: LawFirmReminder };

export type FirmReminderCrud = {
  editor: FirmReminderEditor;
  /** The record open for editing, or null when creating/closed. */
  editingRecord: LawFirmReminder | null;
  form: UseFormReturn<FirmReminderDraft>;
  isSaving: boolean;
  isDeleting: boolean;
  isStatusBusy: boolean;
  openCreate: (initial?: Partial<FirmReminderDraft>) => void;
  openEdit: (record: LawFirmReminder) => void;
  close: () => void;
  /** Persists already-validated values (for the sheet's `onSubmit`). */
  submit: SubmitHandler<FirmReminderDraft>;
  deleteCurrent: () => Promise<boolean>;
  setStatus: (status: ReminderStatus) => Promise<void>;
};

/** Refetch anything that shows reminders. */
export const invalidateReminderViews = async (): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["firm-reminders"] }),
    queryClient.invalidateQueries({ queryKey: ["dashboard"] })
  ]);
};

export const useFirmReminderCrud = ({
  onChanged
}: { onChanged?: () => void } = {}): FirmReminderCrud => {
  const [editor, setEditor] = useState<FirmReminderEditor>({ mode: "closed" });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStatusBusy, setIsStatusBusy] = useState(false);

  const form = useForm<FirmReminderDraft>({
    resolver: zodResolver(firmReminderDraftSchema),
    defaultValues: emptyFirmReminderDraft(),
    mode: "onSubmit",
    reValidateMode: "onChange"
  });

  const editingRecord = useMemo(
    () => (editor.mode === "edit" ? editor.record : null),
    [editor]
  );

  const changed = useCallback(async () => {
    await invalidateReminderViews();
    onChanged?.();
  }, [onChanged]);

  const openCreate = useCallback(
    (initial: Partial<FirmReminderDraft> = {}) => {
      form.reset({ ...emptyFirmReminderDraft(), ...initial });
      setEditor({ mode: "create" });
    },
    [form]
  );

  const openEdit = useCallback(
    (record: LawFirmReminder) => {
      form.reset({
        ...reminderToDraft(record),
        clientId: record.foreign_national_id ?? ""
      });
      setEditor({ mode: "edit", record });
    },
    [form]
  );

  const close = useCallback(() => {
    setEditor({ mode: "closed" });
    form.reset(emptyFirmReminderDraft());
  }, [form]);

  const submit: SubmitHandler<FirmReminderDraft> = useCallback(
    async (draft) => {
      setIsSaving(true);
      try {
        const payload = reminderDraftToPayload(draft);
        const clientId = draft.clientId.trim();
        if (editor.mode === "edit") {
          await updateEntry(editor.record, payload);
          toast.success("Entry updated");
        } else {
          await (clientId
            ? ForeignNationalsAPI.createReminder(clientId, payload)
            : LawFirmRemindersAPI.create({
                ...payload,
                shared_with_client: false
              }));
          toast.success("Entry added");
        }
        await changed();
        close();
      } catch (e) {
        toast.error(
          editor.mode === "edit"
            ? "Couldn't update entry"
            : "Couldn't add entry",
          { description: e instanceof Error ? e.message : undefined }
        );
      } finally {
        setIsSaving(false);
      }
    },
    [changed, close, editor]
  );

  const deleteCurrent = useCallback(async (): Promise<boolean> => {
    if (editor.mode !== "edit") return false;
    setIsDeleting(true);
    try {
      const { foreign_national_id: clientId, reminder_id } = editor.record;
      await (clientId
        ? ForeignNationalsAPI.deleteReminder(clientId, reminder_id)
        : LawFirmRemindersAPI.remove(reminder_id));
      toast.success("Entry deleted");
      await changed();
      close();
      return true;
    } catch (e) {
      toast.error("Couldn't delete entry", {
        description: e instanceof Error ? e.message : undefined
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [changed, close, editor]);

  const setStatus = useCallback(
    async (status: ReminderStatus) => {
      if (editor.mode !== "edit") return;
      setIsStatusBusy(true);
      try {
        await updateEntry(editor.record, { status });
        toast.success(
          status === "COMPLETED"
            ? "Marked complete"
            : status === "DISMISSED"
            ? "Dismissed"
            : "Re-opened"
        );
        await changed();
        close();
      } catch (e) {
        toast.error("Couldn't update", {
          description: e instanceof Error ? e.message : undefined
        });
      } finally {
        setIsStatusBusy(false);
      }
    },
    [changed, close, editor]
  );

  return {
    editor,
    editingRecord,
    form,
    isSaving,
    isDeleting,
    isStatusBusy,
    openCreate,
    openEdit,
    close,
    submit,
    deleteCurrent,
    setStatus
  };
};
